import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { noop } from '@/utils/noop';
import { useFileUploader } from '@/component/FileUpload/useFileUpload';
import { ViewPracticalAssessmentVideoSumbissionForm } from '@/administration/pages/Course/pages/assessments/practicalAssessmentVideo/variants/View/ViewPracticalAssessmentVideoSumbissionForm';
import { UploadedFileVideo } from '@/component/FileUpload/types';
import { useCurrentUser } from '@/store/currentUser';
import {
  RUserLearningSubmissionReview,
  RUserLearningSubmissionReviewAssignment,
  useUserLearningSubmissionsQueryRemote,
} from '@/store/v2';
import { Skeleton } from '@/ui/Skeleton';
import { ViewPracticalAssessmentVideoSubmissionResult } from '@/administration/pages/Course/pages/assessments/practicalAssessmentVideo/variants/View/ViewPracticalAssessmentVideoSubmissionResult';
import { ViewPracticalAssessmentVideoReadyToSubmit } from '@/administration/pages/Course/pages/assessments/practicalAssessmentVideo/variants/View/ViewPracticalAssessmentVideoReadyToSubmit';
import { SubmissionResultData } from '@/administration/pages/Course/pages/assessments/practicalAssessmentVideo/variants/View/types';
import {
  AssessmentTab,
  AssessmentViewerTabs,
} from '@/administration/pages/Course/pages/assessments/common/tabs/AssessmentViewerTabs';
import { AssessmentSubmitted } from '@/administration/pages/Course/pages/assessments/common/banners';

type ViewPracticalAssessmentVideoSubmissionProps = {
  spaceId: number;
  pageRemoteId?: number;
  learningId?: number;
  onCompleted: () => void;
  peerReviewEnabled: boolean;
};

const ReadyToSubmitContainerStyled = styled.div`
  min-height: 505px;
  width: 100%;
  display: flex;

  & iframe {
    border-radius: 0px 0px 6px 6px;
  }
`;

export const ViewPracticalAssessmentVideoSubmission = ({
  spaceId,
  pageRemoteId,
  learningId,
  peerReviewEnabled,
  onCompleted,
}: ViewPracticalAssessmentVideoSubmissionProps): JSX.Element => {
  const [activeTab, setActiveTab] = useState(AssessmentTab.MySubmission);
  const [uploadedVideo, setUploadedVideo] = useState<UploadedFileVideo | undefined>();
  const [assessmentsToReview, setAssessmentsToReview] = useState<RUserLearningSubmissionReviewAssignment[]>([]);
  const [receivedReviews, setReceivedReviews] = useState<RUserLearningSubmissionReview[]>([]);
  const [videoLink, setVideoLink] = useState<string | undefined>();
  const { user } = useCurrentUser();

  const [submissionResult, setSubmissionResult] = useState<{
    createdAt: string;
    data: SubmissionResultData;
  }>();

  const canSubmit = Boolean(uploadedVideo?.vimeoId || videoLink);
  const hasReceivedReviews = Boolean(receivedReviews.length);
  const hasSubmitted = Boolean(submissionResult);
  const hasReviewedPeers = useMemo(
    () =>
      Boolean(assessmentsToReview.length) &&
      assessmentsToReview.every((assessment) => assessment.submission.reviews.some((review) => review.reviewerId === user?.id)),
    [assessmentsToReview, user],
  );

  const shouldShowSubmittedBanner = hasSubmitted && peerReviewEnabled;

  const {
    data: remoteSubmissions,
    loading: loadingSubmissions,
    refetch,
  } = useUserLearningSubmissionsQueryRemote({
    variables: {
      input: {
        learningIdsIN: learningId ? [learningId] : [],
        userIdsIN: user ? [user.id] : [],
        pageIdsIN: pageRemoteId ? [pageRemoteId] : [],
      },
    },
    skip: !pageRemoteId || !learningId || !user?.id,
  });

  const videoUpload = useFileUploader<'video'>({
    spaceId,
    type: 'video',
    onUploadStart: noop,
    onUploadSuccess: ({ vimeoId, ratio, thumbnail, duration }) => {
      setUploadedVideo({
        vimeoId,
        ratio,
        thumbnail,
        duration,
      });
    },
  });

  useEffect(() => {
    if (remoteSubmissions?.userLearningSubmissions.length) {
      const currentRemoteSubmission = remoteSubmissions.userLearningSubmissions[0];

      if (currentRemoteSubmission.reviewAssignments) {
        setAssessmentsToReview(currentRemoteSubmission.reviewAssignments as RUserLearningSubmissionReviewAssignment[]);
      }

      if (currentRemoteSubmission.reviews) {
        setReceivedReviews(currentRemoteSubmission.reviews);
      }

      if (currentRemoteSubmission?.data?.videoLink) {
        setSubmissionResult({
          createdAt: new Date(currentRemoteSubmission.createdAt).toISOString(),
          data: {
            videoLink: currentRemoteSubmission.data.videoLink,
            type: 'embed',
          },
        });
      } else if (currentRemoteSubmission?.data?.vimeoId) {
        setSubmissionResult({
          createdAt: new Date(currentRemoteSubmission.createdAt).toISOString(),
          data: {
            vimeoId: currentRemoteSubmission?.data?.vimeoId,
            duration: currentRemoteSubmission?.data?.duration,
            thumbnail: currentRemoteSubmission?.data?.thumbnail,
            ratio: currentRemoteSubmission?.data?.ratio,
            type: 'vimeo',
          },
        });
      }
    }
  }, [remoteSubmissions]);

  useEffect(() => {
    if (!hasSubmitted) return;

    if (peerReviewEnabled && !hasReviewedPeers) return;

    onCompleted();
  }, [hasSubmitted]);

  const handleLinkSubmit = (link: string) => {
    setVideoLink(link);
  };

  const handleReplace = () => {
    setVideoLink(undefined);
    setUploadedVideo(undefined);
  };

  const handleActiveTabChange = (tabValue: number) => {
    setActiveTab(tabValue);
  };

  const renderSubmissionFormSection = () => {
    if (loadingSubmissions) {
      return <Skeleton height={505} />;
    }

    if (submissionResult?.data) {
      return (
        <ViewPracticalAssessmentVideoSubmissionResult
          submissionResultData={submissionResult.data}
          createdAt={submissionResult.createdAt}
          receivedReviews={receivedReviews}
          peerReviewEnabled={peerReviewEnabled}
        />
      );
    }

    return (
      <ReadyToSubmitContainerStyled>
        {canSubmit ? (
          <ViewPracticalAssessmentVideoReadyToSubmit
            videoLink={videoLink}
            uploadedVideo={uploadedVideo}
            learningId={learningId}
            pageRemoteId={pageRemoteId}
            onReplace={handleReplace}
            onSubmitted={() => {
              refetch();
            }}
          />
        ) : (
          <ViewPracticalAssessmentVideoSumbissionForm onLinkSubmit={handleLinkSubmit} videoUpload={videoUpload} />
        )}
      </ReadyToSubmitContainerStyled>
    );
  };

  return (
    <>
      {shouldShowSubmittedBanner && (
        <AssessmentSubmitted
          hasReceivedReviews={hasReceivedReviews}
          hasReviewedPeers={hasReviewedPeers}
          onReviewPeersClick={() => setActiveTab(AssessmentTab.PeersToReview)}
          onCheckReviewClick={() => setActiveTab(AssessmentTab.MySubmission)}
        />
      )}
      {peerReviewEnabled ? (
        <AssessmentViewerTabs
          submissionFormSection={renderSubmissionFormSection()}
          submitted={!!submissionResult}
          assessmentsToReview={assessmentsToReview}
          pageRemoteId={pageRemoteId}
          type="video"
          activeTab={activeTab}
          onActiveTabChange={handleActiveTabChange}
          spaceId={spaceId}
        />
      ) : (
        <>{renderSubmissionFormSection()}</>
      )}
    </>
  );
};
